import type { UnknownAction } from "@reduxjs/toolkit";
import { createReducer } from "@reduxjs/toolkit";
import { assocIn, dissocIn } from "icepick";
import _ from "underscore";

import {
  createDashboardPublicLink,
  deleteDashboardPublicLink,
  updateDashboard,
  updateDashboardEmbeddingParams,
  updateDashboardEnableEmbedding,
} from "metabase/api";
import { handleActions } from "metabase/redux";
import { CARD_UPDATED } from "metabase/redux/cards";
import {
  INITIALIZE,
  RESET,
  SET_PARAMETER_VALUES,
  SHOW_ADD_PARAMETER_POPOVER,
  initialize,
  reset,
} from "metabase/redux/dashboard";
import {
  NAVIGATE_BACK_TO_DASHBOARD,
  REVERT_CARD_TO_REVISION,
} from "metabase/redux/query-builder";
import type {
  DashboardLinkTargets,
  StoreDashboard,
} from "metabase/redux/store/dashboard";
import type { UiParameter } from "metabase-lib/v1/parameters/types";
import type {
  Card,
  Dashboard,
  ParameterId,
  ParameterValueOrArray,
  ParameterValuesMap,
  Revision,
} from "metabase-types/api";

import {
  CLOSE_SIDEBAR,
  HIDE_ADD_PARAMETER_POPOVER,
  REMOVE_PARAMETER,
  RESET_PARAMETERS,
  SET_EDITING_DASHBOARD,
  SET_PARAMETER_VALUE,
  SHOW_AUTO_APPLY_FILTERS_TOAST,
  addCardToDash,
  addDashcardIdsToLoadingQueue,
  addManyCardsToDash,
  cancelFetchCardData,
  clearCardData,
  dashboardLayoutFetched,
  fetchCardDataAction,
  fetchDashboard,
  fetchDashboardCardDataAction,
  markCardAsSlow,
  setDashboardAttributes,
  setDocumentTitle,
  setShowLoadingCompleteFavicon,
  setSidebar,
} from "./actions";
import {
  deselectTimelineEvents,
  markTimelineEventsShown,
  selectTimelineEvents,
  setDashCardTimelineEventsEnabled,
  setDashCardTimelineEventsVisibility,
} from "./actions/timeline-events";
import { INITIAL_DASHBOARD_STATE, SIDEBAR_NAME } from "./constants";
import { syncParametersAndEmbeddingParams } from "./utils";

export const dashboardId = createReducer(
  INITIAL_DASHBOARD_STATE.dashboardId,
  (builder) => {
    builder.addCase(INITIALIZE, () => null);
    builder.addCase(
      fetchDashboard.fulfilled,
      (_state, { payload }) => payload.dashboardId,
    );
    builder.addCase(RESET, () => null);
  },
);

// TODO: double check if this reducer is used
export const missingActionParameters = handleActions(
  {
    [INITIALIZE]: {
      next: () => null,
    },
    [RESET]: {
      next: () => null,
    },
  },
  INITIAL_DASHBOARD_STATE.missingActionParameters,
);

export const autoApplyFilters = createReducer(
  INITIAL_DASHBOARD_STATE.autoApplyFilters,
  (builder) => {
    builder.addCase<
      string,
      {
        type: string;
        payload: {
          toastId: number | null;
          dashboardId: number | null;
        };
      }
    >(SHOW_AUTO_APPLY_FILTERS_TOAST, (state, { payload }) => {
      const { toastId, dashboardId } = payload;

      state.toastId = toastId;
      state.toastDashboardId = dashboardId;
    });
  },
);

export const slowCards = createReducer(
  INITIAL_DASHBOARD_STATE.slowCards,
  (builder) => {
    builder.addCase(markCardAsSlow, (state, { payload: { id, result } }) => ({
      ...state,
      [id]: result,
    }));
  },
);

export const isNavigatingBackToDashboard = handleActions(
  {
    [NAVIGATE_BACK_TO_DASHBOARD]: () => true,
    [RESET]: () => false,
  },
  INITIAL_DASHBOARD_STATE.isNavigatingBackToDashboard,
);

export const isAddParameterPopoverOpen = handleActions(
  {
    [SHOW_ADD_PARAMETER_POPOVER]: () => true,
    [HIDE_ADD_PARAMETER_POPOVER]: () => false,
    [INITIALIZE]: () => false,
    [RESET]: () => false,
  },
  INITIAL_DASHBOARD_STATE.isAddParameterPopoverOpen,
);

export const editingDashboard = handleActions(
  {
    [INITIALIZE]: { next: () => INITIAL_DASHBOARD_STATE.editingDashboard },
    [SET_EDITING_DASHBOARD]: {
      next: (state, { payload }) => {
        // Only update the dashboard in the state if the new dashboard differs from the current one.
        // This prevents the case where this function is accidentally called with an edited/dirty dashboard,
        // preventing the diff logic in our save flow from properly detecting that were changes.
        if (payload !== null && state?.id === payload?.id) {
          console.warn(
            "The editingDashboard state should not be set to a newer version of the same dashboard. This can produce subtle bugs for detecting how dashboards have changed over time. Skipping updating state and using old editingDashboard state.",
          );
          return state;
        }

        return payload ?? null;
      },
    },
    [RESET]: { next: () => INITIAL_DASHBOARD_STATE.editingDashboard },
  },
  INITIAL_DASHBOARD_STATE.editingDashboard,
);

export const loadingControls = createReducer(
  INITIAL_DASHBOARD_STATE.loadingControls,
  (builder) => {
    builder.addCase(setDocumentTitle, (state, { payload }) => {
      state.documentTitle = payload;
    });

    builder.addCase(setShowLoadingCompleteFavicon, (state, { payload }) => {
      state.showLoadCompleteFavicon = payload;
    });

    builder.addCase(RESET, () => INITIAL_DASHBOARD_STATE.loadingControls);

    builder.addCase(INITIALIZE, () => INITIAL_DASHBOARD_STATE.loadingControls);

    builder.addCase(fetchDashboard.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(fetchDashboard.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(fetchDashboard.rejected, (state) => {
      state.isLoading = false;
    });
  },
);

const DEFAULT_SIDEBAR = { props: {} };
export const sidebar = createReducer(
  INITIAL_DASHBOARD_STATE.sidebar,
  (builder) => {
    builder.addCase(INITIALIZE, () => DEFAULT_SIDEBAR);
    builder.addCase(RESET, () => DEFAULT_SIDEBAR);
    builder.addCase(REMOVE_PARAMETER, () => DEFAULT_SIDEBAR);
    builder.addCase(SET_EDITING_DASHBOARD, () => DEFAULT_SIDEBAR);
    builder.addCase(CLOSE_SIDEBAR, () => DEFAULT_SIDEBAR);

    builder.addCase(setSidebar, (_state, { payload: { name, props } }) => ({
      name,
      props: props || {},
    }));
  },
);

export const timelineEvents = createReducer(
  INITIAL_DASHBOARD_STATE.timelineEvents,
  (builder) => {
    builder.addCase(INITIALIZE, () => INITIAL_DASHBOARD_STATE.timelineEvents);
    builder.addCase(RESET, () => INITIAL_DASHBOARD_STATE.timelineEvents);
    builder.addCase(SET_EDITING_DASHBOARD, (state) => ({
      ...INITIAL_DASHBOARD_STATE.timelineEvents,
      hasTrackedEventsShown: state.hasTrackedEventsShown,
    }));
    builder.addCase(CLOSE_SIDEBAR, (state) => {
      state.selection = null;
    });
    builder.addCase(setSidebar, (state, { payload: { name } }) => {
      if (name !== SIDEBAR_NAME.events) {
        state.selection = null;
      }
    });
    builder.addCase(setDashCardTimelineEventsVisibility, (state, action) => {
      state.overrides = { ...state.overrides, ...action.payload };
    });
    builder.addCase(
      setDashCardTimelineEventsEnabled,
      (state, { payload: { dashcardId, isEnabled } }) => {
        if (state.enabledByDashCard[dashcardId] !== isEnabled) {
          state.enabledByDashCard[dashcardId] = isEnabled;
        }
      },
    );
    builder.addCase(selectTimelineEvents, (state, action) => {
      state.selection = action.payload;
    });
    builder.addCase(deselectTimelineEvents, (state) => {
      state.selection = null;
    });
    builder.addCase(markTimelineEventsShown, (state) => {
      state.hasTrackedEventsShown = true;
    });
  },
);

export const parameterValues = createReducer(
  INITIAL_DASHBOARD_STATE.parameterValues,
  (builder) => {
    builder.addCase(
      initialize,
      (state, { payload: { clearCache = true } = {} }) => {
        return clearCache ? {} : state;
      },
    );

    builder.addCase(fetchDashboard.fulfilled, (_state, { payload }) => {
      return payload.parameterValues;
    });

    builder.addCase<
      string,
      {
        type: string;
        payload: {
          id: ParameterId;
          value: ParameterValueOrArray | undefined | null;
          isDraft: boolean;
        };
      }
    >(SET_PARAMETER_VALUE, (state, { payload: { id, value, isDraft } }) => {
      if (!isDraft) {
        state[id] = value;
      }
    });

    builder.addCase<
      string,
      {
        type: string;
        payload: ParameterValuesMap;
      }
    >(SET_PARAMETER_VALUES, (_state, { payload }) => {
      return payload;
    });

    builder.addCase<
      string,
      {
        type: string;
        payload: UiParameter[];
      }
    >(RESET_PARAMETERS, (state, { payload: parameters }) => {
      for (const parameter of parameters) {
        const { id, value } = parameter;
        state[id] = value;
      }
    });

    builder.addCase<string, { type: string; payload: { id: ParameterId } }>(
      REMOVE_PARAMETER,
      (state, { payload: { id } }) => {
        delete state[id];
      },
    );
  },
);

function newDashboard(
  before: StoreDashboard,
  after: Partial<Dashboard>,
  isDirty: boolean,
): StoreDashboard {
  return {
    ...before,
    // mimic the StoreDashboard type - this function is only made to update attributes
    // rather than deep values like dashcards or tabs
    ..._.omit(after, "dashcards", "tabs"),
    embedding_params: syncParametersAndEmbeddingParams(before, after),
    isDirty,
  };
}

// Replaced rather than merged: the targets belong to the dashboard being
// loaded, and a stale entry would let a removed click behavior keep resolving.
export function linkTargets(
  state: DashboardLinkTargets = INITIAL_DASHBOARD_STATE.linkTargets,
  action: UnknownAction,
): DashboardLinkTargets {
  if (fetchDashboard.fulfilled.match(action)) {
    return action.payload.linkTargets;
  }
  if (action.type === RESET) {
    return INITIAL_DASHBOARD_STATE.linkTargets;
  }
  return state;
}

export const dashboards = createReducer(
  INITIAL_DASHBOARD_STATE.dashboards,
  (builder) => {
    builder
      .addCase(fetchDashboard.fulfilled, (state, { payload }) => ({
        ...state,
        ...payload.entities.dashboard,
      }))
      .addCase(dashboardLayoutFetched, (state, { payload }) => ({
        ...state,
        ...payload.dashboard,
      }))
      .addCase(
        setDashboardAttributes,
        (state, { payload: { id, attributes, isDirty = true } }) => {
          // Cast to avoid infinite type instantiation error.
          const dashboards = state as Record<string, StoreDashboard>;
          dashboards[id] = newDashboard(dashboards[id], attributes, isDirty);
        },
      )
      .addCase(addCardToDash, (state, { payload: dashcard }) => {
        state[dashcard.dashboard_id].dashcards.push(dashcard.id);
      })
      .addCase(addManyCardsToDash, (state, { payload: dashcards }) => {
        const [{ dashboard_id }] = dashcards;
        const dashcardIds = dashcards.map(({ id }) => id);
        state[dashboard_id].dashcards.push(...dashcardIds);
      })
      .addMatcher(updateDashboard.matchFulfilled, (state, { payload }) => {
        const draftDashboard = state[payload.id];
        if (draftDashboard) {
          draftDashboard.collection_id = payload.collection_id;
          draftDashboard.collection = payload.collection;
        }
      })
      .addMatcher(
        createDashboardPublicLink.matchFulfilled,
        (state, { payload }) =>
          assocIn(state, [payload.id, "public_uuid"], payload.uuid),
      )
      .addMatcher(
        deleteDashboardPublicLink.matchFulfilled,
        (state, { payload }) =>
          assocIn(state, [payload.id, "public_uuid"], null),
      )
      .addMatcher(
        updateDashboardEmbeddingParams.matchFulfilled,
        (state, { payload }) => {
          const dashboard = state[payload.id];
          dashboard.embedding_params = payload.embedding_params;
          dashboard.embedding_type = payload.embedding_type;
        },
      )
      .addMatcher(
        updateDashboardEnableEmbedding.matchFulfilled,
        (state, { payload }) => {
          const dashboard = state[payload.id];
          dashboard.enable_embedding = payload.enable_embedding;
          dashboard.embedding_type = payload.embedding_type;
          dashboard.initially_published_at = payload.initially_published_at;
        },
      );
  },
);

export const loadingDashCards = createReducer(
  INITIAL_DASHBOARD_STATE.loadingDashCards,
  (builder) => {
    builder
      .addCase(initialize, (state) => ({
        ...state,
        loadingStatus: "idle",
      }))
      .addCase(fetchDashboardCardDataAction, (state, action) => {
        const { currentTime, loadingIds } = action.payload;
        return {
          ...state,
          loadingIds,
          loadingStatus: loadingIds.length > 0 ? "running" : "complete",
          startTime: loadingIds.length > 0 ? currentTime : null,
        };
      })
      .addCase(addDashcardIdsToLoadingQueue, (state, action) => {
        const { dashcard_id } = action.payload;
        const loadingIds = !state.loadingIds.includes(dashcard_id)
          ? state.loadingIds.concat(dashcard_id)
          : state.loadingIds;
        return {
          ...state,
          loadingIds,
        };
      })
      .addCase(fetchCardDataAction.fulfilled, (state, { payload = {} }) => {
        const { dashcard_id, currentTime } = payload;
        if (dashcard_id) {
          const loadingIds = state.loadingIds.filter(
            (id) => id !== dashcard_id,
          );
          return {
            ...state,
            loadingIds,
            ...(loadingIds.length === 0
              ? { endTime: currentTime, loadingStatus: "complete" }
              : {}),
          };
        }
      })
      .addCase(cancelFetchCardData, (state, action) => {
        const { dashcard_id } = action.payload;
        const loadingIds = state.loadingIds.filter((id) => id !== dashcard_id);
        return {
          ...state,
          loadingIds,
          ...(loadingIds.length === 0 ? { startTime: null } : {}),
        };
      })
      .addCase(reset, (state) => ({
        ...state,
        loadingStatus: "idle",
      }));
  },
);

export const dashcardData = createReducer(
  INITIAL_DASHBOARD_STATE.dashcardData,
  (builder) => {
    builder
      .addCase(initialize, (state, action) => {
        const { clearCache = true } = action.payload ?? {};
        return clearCache ? {} : state;
      })
      .addCase(fetchCardDataAction.fulfilled, (state, action) => {
        const { dashcard_id, card_id, result } = action.payload ?? {};
        if (dashcard_id && card_id && result != null) {
          return assocIn(state, [dashcard_id, card_id], result);
        }
      })
      .addCase(clearCardData, (state, action) => {
        const { cardId, dashcardId } = action.payload;
        return dissocIn(state, [dashcardId, cardId]);
      })
      .addCase<string, { type: string; payload: { object?: Card } }>(
        CARD_UPDATED,
        (state, { payload: { object: card } }) => {
          if (card) {
            const { id } = card;
            for (const dashcardId in state) {
              delete state[dashcardId][id];
            }
          }
        },
      )
      .addCase<string, { type: string; payload: Revision }>(
        REVERT_CARD_TO_REVISION,
        (state, action) => {
          const { id } = action.payload;
          if (id != null) {
            for (const dashcardId in state) {
              delete state[dashcardId][id];
            }
          }
        },
      );
  },
);
